import React, { ComponentProps, useCallback, useState } from 'react';

import fileDownload from 'react-file-download';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_VENDOR_ADMIN, ROLE_VENDOR_USER } from '~/consts/roles';

import { useDownloadManifest } from '~/hooks/apis/tours/useDownloadManifest';
import { useViewManifest } from '~/hooks/apis/tours/useViewManifest';

import { ManifestViewData } from '~/services/ToursService';

const TRIGGERED_DOWNLOAD_HINT =
  'File is encrypted. Use the password below to open the archive. The password is only valid for this file and should not be shared externally.';

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

function formatCellValue(value: string | number | boolean): string {
  const str = String(value ?? '');
  if (ISO_DATE_REGEX.test(str)) {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  return str;
}

export default function ToursManifestPage() {
  const [tourId, setTourId] = useState<string>('');
  const [departureName, setDepartureName] = useState<string>('');
  const [password, setPassword] = useState<string | null>(null);
  const [downloadedFilename, setDownloadedFilename] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [copiedPassword, setCopiedPassword] = useState<boolean>(false);

  const [manifestData, setManifestData] = useState<ManifestViewData | null>(null);

  const downloadMutation = useDownloadManifest();
  const viewMutation = useViewManifest();

  const resetResultState = useCallback(() => {
    setPassword(null);
    setDownloadedFilename(null);
    setShowPassword(false);
    setCopiedPassword(false);
    setManifestData(null);
  }, []);

  const handleSubmit = useCallback<NonNullable<ComponentProps<'form'>['onSubmit']>>(
    async (event) => {
      event.preventDefault();

      const trimmedTourId = tourId.trim();
      const trimmedDepartureName = departureName.trim();

      if (!trimmedTourId || !trimmedDepartureName) {
        return;
      }

      resetResultState();

      try {
        const result = await downloadMutation.mutateAsync({
          tourId: trimmedTourId,
          departureName: trimmedDepartureName,
        });

        fileDownload(result.blob, result.filename);
        setDownloadedFilename(result.filename);
        setPassword(result.password);
      } catch {
        // Error state is surfaced via downloadMutation.error below.
      }
    },
    [tourId, departureName, downloadMutation, resetResultState],
  );

  const handleCopyPassword = useCallback(async () => {
    if (!password) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = password;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedPassword(true);
      window.setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Failed to copy manifest password:', err);
    }
  }, [password]);

  const handleView = useCallback(async () => {
    const trimmedTourId = tourId.trim();
    const trimmedDepartureName = departureName.trim();
    if (!trimmedTourId || !trimmedDepartureName) return;

    resetResultState();

    try {
      const result = await viewMutation.mutateAsync({
        tourId: trimmedTourId,
        departureName: trimmedDepartureName,
      });
      setManifestData(result);
    } catch {
      // Error surfaced via viewMutation.error
    }
  }, [tourId, departureName, viewMutation, resetResultState]);

  const canSubmit =
    tourId.trim().length > 0 &&
    departureName.trim().length > 0 &&
    !downloadMutation.isPending &&
    !viewMutation.isPending;

  const errorMessage =
    downloadMutation.isError && downloadMutation.error instanceof Error
      ? downloadMutation.error.message
      : downloadMutation.isError
        ? 'Failed to download manifest.'
        : null;

  const viewErrorMessage =
    viewMutation.isError && viewMutation.error instanceof Error
      ? viewMutation.error.message
      : viewMutation.isError
        ? 'Failed to view manifest.'
        : null;

  return (
    <Container>
      <PageHeader title="Departure Manifests" subtitle="View or download the departure manifest for a tour deal" />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Opportunity ID"
                  placeholder="e.g. 006RF00000UzMn7YAF"
                  value={tourId}
                  onChange={(event) => setTourId(event.target.value)}
                  fullWidth
                  required
                  autoFocus
                  disabled={downloadMutation.isPending || viewMutation.isPending}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Departure name"
                  placeholder="e.g. SD-0019083"
                  value={departureName}
                  onChange={(event) => setDepartureName(event.target.value)}
                  fullWidth
                  required
                  disabled={downloadMutation.isPending || viewMutation.isPending}
                />
              </Grid>
            </Grid>

            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!canSubmit}
                  onClick={handleView}
                  startIcon={viewMutation.isPending ? <CircularProgress color="inherit" size={16} /> : <PreviewIcon />}
                >
                  {viewMutation.isPending ? 'Loading…' : 'View manifest'}
                </Button>
                <PermissionedComponent requiredRoles={[ROLE_VENDOR_ADMIN, ROLE_VENDOR_USER]}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!canSubmit}
                    startIcon={
                      downloadMutation.isPending ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />
                    }
                  >
                    {downloadMutation.isPending ? 'Downloading…' : 'Download manifest'}
                  </Button>
                </PermissionedComponent>
              </Stack>
            </Box>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {viewErrorMessage && <Alert severity="error">{viewErrorMessage}</Alert>}

            {password && downloadedFilename && (
              <Alert severity="success" icon={false}>
                <Stack spacing={2}>
                  <Typography variant="body2">
                    Downloaded <strong>{downloadedFilename}</strong>. {TRIGGERED_DOWNLOAD_HINT}
                  </Typography>

                  <TextField
                    label="Password"
                    value={password}
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                              <IconButton
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                onClick={() => setShowPassword((prev) => !prev)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={copiedPassword ? 'Copied!' : 'Copy password'}>
                              <IconButton aria-label="Copy password" onClick={handleCopyPassword} edge="end">
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Alert>
            )}
          </Stack>
        </form>

        {manifestData && (
          <>
            <Typography variant="h6" sx={{ mt: 3 }}>
              Manifest Overview
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={1}>
                {Object.entries(manifestData.overview).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <Grid size={{ xs: 4, md: 3 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {key}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8, md: 9 }}>
                      <Typography variant="body2">{formatCellValue(value)}</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>
            <Typography variant="h6">Passenger Details ({manifestData.data.length} rows)</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {manifestData.headings.map((heading, colIndex) => (
                      <TableCell
                        key={heading}
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          position: 'sticky',
                          top: 0,
                          zIndex: colIndex === 0 ? 3 : 2,
                          ...(colIndex === 0 && {
                            left: 0,
                            borderRight: '2px solid',
                            borderRightColor: 'divider',
                          }),
                        }}
                      >
                        {heading.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manifestData.data.map((row, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        bgcolor: index % 2 === 0 ? 'background.default' : 'action.hover',
                      }}
                    >
                      {manifestData.headings.map((heading, colIndex) => (
                        <TableCell
                          key={heading}
                          sx={{
                            whiteSpace: 'nowrap',
                            ...(colIndex === 0 && {
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              bgcolor: index % 2 === 0 ? 'background.default' : 'grey.100',
                              borderRight: '2px solid',
                              borderRightColor: 'divider',
                              fontWeight: 'medium',
                            }),
                          }}
                        >
                          {formatCellValue(row[heading])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Container>
  );
}
